import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { setDpassSignupRecord } from './use-dpass-signup-record';

import type { AxiosInstance } from 'axios';

interface CreateDpassMembershipRequest {
  email: string;
  from?: string;
}

const createDpassMembership = (
  client: AxiosInstance,
  { email, from }: CreateDpassMembershipRequest,
) => {
  return client.post('/v2/auth/init', { email, from });
};

const useCreateDpassMembership = ({ client }: { client: AxiosInstance }) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (params: CreateDpassMembershipRequest) => {
      return createDpassMembership(client, params);
    },
    onSuccess(_, params) {
      setDpassSignupRecord({ email: params.email, from: params.from });
    },
  });

  return {
    createDpassMembership: mutateAsync,
    createDpassMembershipLoading: isPending,
  };
};

type DpassSignupStatus = 'success' | 'failed';

interface UseDpassSignupProps {
  client: AxiosInstance;
  from: string;
  onDialogDismiss: () => void;
  onSignupResult?: (params: {
    email: string;
    status: DpassSignupStatus;
  }) => void;
}

const useDpassSignup = ({
  client,
  from,
  onDialogDismiss,
  onSignupResult,
}: UseDpassSignupProps) => {
  const { createDpassMembership, createDpassMembershipLoading } =
    useCreateDpassMembership({ client });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const emailResolveRef = useRef<(value: string | null) => void>();

  const submitMembership = async (
    email: string,
  ): Promise<DpassSignupStatus> => {
    try {
      await createDpassMembership({ email, from });
      return 'success';
    } catch (error) {
      console.error('create dpass membership error', error);
      return 'failed';
    }
  };

  const requestDpassSignup = async (emailFromReceipt: string | undefined) => {
    if (emailFromReceipt) {
      await submitMembership(emailFromReceipt);
      return emailFromReceipt;
    }

    setIsDialogOpen(true);
    return new Promise<string | null>((resolve) => {
      emailResolveRef.current = resolve;
    });
  };

  const handleDialogSubmit = async (email: string) => {
    const status = await submitMembership(email);
    onSignupResult?.({ email, status });
    setIsDialogOpen(false);
    emailResolveRef.current?.(email);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    onDialogDismiss?.();
    emailResolveRef.current?.(null);
  };

  return {
    isDpassDialogOpen: isDialogOpen,
    isDpassSubmitting: createDpassMembershipLoading,
    onDpassDialogClose: handleDialogClose,
    onDpassDialogSubmit: handleDialogSubmit,
    requestDpassSignup,
  };
};

export { useDpassSignup };
export type { DpassSignupStatus };
